import { FormEvent, useEffect, useRef, useState } from 'react';
import { Box, Button, CircularProgress, Stack, TextField, Typography } from '@mui/material';
import { IconBrandTelegram } from '@tabler/icons-react';

import type { AIManagerTelegramAuthSession } from 'src/services/AIManagersService';

type TelegramAuthErrorPayload = {
    detail?: string;
    code?: string;
    retry_after?: number;
};

const NEW_CODE_ERROR_CODES = new Set([
    'code_expired',
    'new_code_required',
    'session_expired',
    'session_not_found',
    'telegram_verify_failed',
    'telegram_timeout',
    'too_many_attempts',
]);

function readAuthError(error: unknown, fallback: string) {
    const payload = (error as { response?: { data?: TelegramAuthErrorPayload } })?.response?.data;
    return {
        code: String(payload?.code || ''),
        message: String(payload?.detail || fallback),
        retryAfter: Math.max(0, Number(payload?.retry_after) || 0),
    };
}

export type TelegramAuthClient = {
    startTelegramAuth: (managerId: string, phoneNumber: string) => Promise<AIManagerTelegramAuthSession>;
    verifyTelegramAuth: (payload: {
        sessionToken: string;
        code?: string;
        password?: string;
    }) => Promise<AIManagerTelegramAuthSession>;
};

export function TelegramConnectForm({
    managerId,
    onConnected,
    service,
}: {
    managerId: string;
    onConnected: () => void;
    service: TelegramAuthClient;
}) {
    const [phone, setPhone] = useState('');
    const [code, setCode] = useState('');
    const [password, setPassword] = useState('');
    const [authSession, setAuthSession] = useState<AIManagerTelegramAuthSession | null>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState('');
    const [needsNewCode, setNeedsNewCode] = useState(false);
    const [retryAfter, setRetryAfter] = useState(0);
    const requestVersion = useRef(0);

    useEffect(() => {
        requestVersion.current += 1;
        setAuthSession(null);
        setPhone('');
        setCode('');
        setPassword('');
        setError('');
        setNeedsNewCode(false);
        setRetryAfter(0);
        setIsSubmitting(false);
        return () => {
            requestVersion.current += 1;
        };
    }, [managerId]);

    useEffect(() => {
        if (retryAfter <= 0) return;
        const timer = window.setInterval(() => setRetryAfter((value) => Math.max(0, value - 1)), 1000);
        return () => window.clearInterval(timer);
    }, [retryAfter]);

    const requestCode = async () => {
        if (!phone.trim() || isSubmitting || retryAfter > 0) return;
        const version = ++requestVersion.current;
        setIsSubmitting(true);
        setError('');
        try {
            const session = await service.startTelegramAuth(managerId, phone.trim());
            if (version !== requestVersion.current) return;
            setAuthSession(session);
            setCode('');
            setPassword('');
            setNeedsNewCode(false);
        } catch (requestError) {
            if (version !== requestVersion.current) return;
            const authError = readAuthError(requestError, 'Не удалось отправить код.');
            setError(authError.message);
            setRetryAfter(authError.retryAfter);
        } finally {
            if (version === requestVersion.current) setIsSubmitting(false);
        }
    };

    const handleVerify = async () => {
        if (!authSession || isSubmitting) return;
        const needsPassword = authSession.status === 'password_required';
        if ((needsPassword && password.length === 0) || (!needsPassword && !code.trim())) return;

        const version = ++requestVersion.current;
        setIsSubmitting(true);
        setError('');
        try {
            const session = await service.verifyTelegramAuth({
                sessionToken: authSession.sessionToken,
                code: code.trim(),
                password,
            });
            if (version !== requestVersion.current) return;
            setAuthSession(session);
            if (session.status === 'completed') onConnected();
        } catch (requestError) {
            if (version !== requestVersion.current) return;
            const authError = readAuthError(requestError, 'Не удалось подтвердить Telegram.');
            setError(authError.code === 'password_required' ? '' : authError.message);
            setRetryAfter(authError.retryAfter);
            if (authError.code === 'password_required' || authError.code === 'invalid_password') {
                setAuthSession((current) =>
                    current ? { ...current, status: 'password_required', lastError: authError.code } : current
                );
                setPassword('');
            }
            if (NEW_CODE_ERROR_CODES.has(authError.code)) setNeedsNewCode(true);
        } finally {
            if (version === requestVersion.current) setIsSubmitting(false);
        }
    };

    const resetSession = (keepPhone: boolean) => {
        requestVersion.current += 1;
        setAuthSession(null);
        setCode('');
        setPassword('');
        setError('');
        setNeedsNewCode(false);
        if (!keepPhone) setPhone('');
    };

    const needsPassword = authSession?.status === 'password_required';
    const retryLabel = retryAfter > 0 ? `Повторить через ${retryAfter} сек.` : 'Отправить новый код';
    const handleSubmit = (event: FormEvent) => {
        event.preventDefault();
        if (!authSession) void requestCode();
        else if (!needsNewCode) void handleVerify();
    };

    return (
        <Box
            component="form"
            onSubmit={handleSubmit}
            sx={{
                bgcolor: 'var(--lm-canvas)',
                border: '1px solid var(--lm-border)',
                borderRadius: '8px',
                p: 1.5,
                width: '100%',
            }}
        >
            <Stack spacing={1.25}>
                <Typography variant="body2" sx={{ fontWeight: 800 }}>
                    Подключение Telegram
                </Typography>
                {!authSession ? (
                    <>
                        <TextField
                            label="Номер телефона"
                            value={phone}
                            onChange={(event) => setPhone(event.target.value)}
                            placeholder="+79991234567"
                            type="tel"
                            autoComplete="tel"
                            size="small"
                            fullWidth
                        />
                        <Button
                            variant="contained"
                            type="submit"
                            disabled={isSubmitting || !phone.trim() || retryAfter > 0}
                            fullWidth
                            startIcon={isSubmitting ? <CircularProgress size={16} color="inherit" /> : <IconBrandTelegram size={16} />}
                        >
                            {retryAfter > 0 ? `Повторить через ${retryAfter} сек.` : 'Получить код'}
                        </Button>
                    </>
                ) : (
                    <>
                        <Typography role="status" aria-live="polite" variant="body2" color="text.secondary">
                            {needsPassword ? 'Введите пароль 2FA.' : `Код отправлен на ${authSession.phoneNumber || phone}.`}
                        </Typography>
                        {!needsNewCode && (
                            <TextField
                                label={needsPassword ? 'Пароль 2FA' : 'Код из Telegram'}
                                value={needsPassword ? password : code}
                                onChange={(event) => (needsPassword ? setPassword(event.target.value) : setCode(event.target.value))}
                                type={needsPassword ? 'password' : 'text'}
                                autoComplete={needsPassword ? 'current-password' : 'one-time-code'}
                                inputProps={needsPassword ? undefined : { inputMode: 'numeric' }}
                                size="small"
                                fullWidth
                            />
                        )}
                        {!needsNewCode && (
                            <Button
                                variant="contained"
                                type="submit"
                                disabled={isSubmitting || (needsPassword ? password.length === 0 : !code.trim())}
                                fullWidth
                            >
                                {isSubmitting ? 'Проверяем...' : 'Подтвердить'}
                            </Button>
                        )}
                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                            <Button
                                variant="text"
                                type="button"
                                disabled={isSubmitting || retryAfter > 0}
                                onClick={() => void requestCode()}
                                fullWidth
                            >
                                {retryLabel}
                            </Button>
                            <Button
                                variant="text"
                                type="button"
                                disabled={isSubmitting}
                                onClick={() => resetSession(false)}
                                fullWidth
                            >
                                Изменить номер
                            </Button>
                        </Stack>
                    </>
                )}
                {error && (
                    <Typography role="alert" aria-live="assertive" variant="body2" color="error.main">
                        {error}
                    </Typography>
                )}
            </Stack>
        </Box>
    );
}
